import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import type { HttpRequestConfig, HttpResponse, IHttpClient } from "@/core/interfaces";

import { getApiBaseUrl } from "@/core/environment";
import { AUTH_TOKEN_COOKIE } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { getCookie } from "@/core/utils/cookie";
import {
  logError,
  logWarning,
  NetworkErrorException,
  APIErrorException,
  readServiceErrorEnvelope,
} from "@/core/services";
import { clearAuthToken } from "@/modules/auth/auth.service";
import { requestActivity } from "./request-activity";

function mapConfig(config?: HttpRequestConfig): AxiosRequestConfig {
  return {
    headers: config?.headers,
    params: config?.params,
    signal: config?.signal,
    responseType: config?.responseType,
  };
}

function mapHeaders(headers: unknown): Record<string, string> {
  if (!headers || typeof headers !== "object") return {};

  return Object.fromEntries(
    Object.entries(headers as Record<string, unknown>)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([name, value]) => [name.toLowerCase(), String(value)]),
  );
}

/**
 * Nas requisições de arquivo o corpo do erro também chega em bytes: decodifica o
 * JSON devolvido pelo serviço para preservar a mensagem.
 */
function readErrorBody(data: unknown): Record<string, unknown> | undefined {
  const body = data instanceof ArrayBuffer ? decodeJson(data) : data;
  return typeof body === "object" && body !== null ? (body as Record<string, unknown>) : undefined;
}

function decodeJson(data: ArrayBuffer): unknown {
  try {
    return JSON.parse(new TextDecoder().decode(data));
  } catch {
    return undefined;
  }
}

function redirectOnSessionExpired(): void {
  window.location.replace(`${window.location.origin}${Paths.LOGIN}`);
}

export class AxiosHttpClient implements IHttpClient {
  private readonly instance: AxiosInstance;

  constructor(baseURL = getApiBaseUrl()) {
    this.instance = axios.create({
      baseURL,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
    });

    this.instance.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
      const token = getCookie(AUTH_TOKEN_COOKIE);
      if (token && cfg.headers) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      requestActivity.begin(cfg.method, cfg.url);
      return cfg;
    });

    this.instance.interceptors.response.use(
      (res) => {
        requestActivity.end(res.config.method, res.config.url);
        return res;
      },
      (error) => {
        requestActivity.end(
          axios.isAxiosError(error) ? error.config?.method : undefined,
          axios.isAxiosError(error) ? error.config?.url : undefined,
        );

        // Cancelada pelo chamador (AbortController: efeito refeito, troca de visão, fechar ficha): não é falha de rede.
        if (axios.isCancel(error)) return Promise.reject(error);

        if (!axios.isAxiosError(error)) {
          logError(error, { component: "HttpClient" });
          return Promise.reject(error);
        }

        const status = error.response?.status;

        // 403 não desloga: segue como APIErrorException para a tela exibir "sem permissão".
        if (status === 401) {
          logWarning("Sessão expirada (401), redirecionando para login", {
            url: error.config?.url,
          });
          clearAuthToken();
          redirectOnSessionExpired();
          return new Promise(() => {});
        }

        if (!error.response) {
          const networkErr = new NetworkErrorException("Erro de conexão com o servidor");
          logError(networkErr, { component: "HttpClient", action: error.config?.url });
          return Promise.reject(networkErr);
        }

        const body = readErrorBody(error.response.data);
        const envelope = readServiceErrorEnvelope(body);
        const apiErr = new APIErrorException(
          status ?? 0,
          envelope.message ?? error.message,
          JSON.stringify(body),
          mapHeaders(error.response.headers),
          envelope.code,
          envelope.details,
        );
        logError(apiErr, { component: "HttpClient", action: error.config?.url });
        return Promise.reject(apiErr);
      },
    );
  }

  async get<T>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.get<T>(url, mapConfig(config));
    return { data: res.data, status: res.status, headers: mapHeaders(res.headers) };
  }

  async post<T>(url: string, body?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.post<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async put<T>(url: string, body?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.put<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async patch<T>(
    url: string,
    body?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    const res = await this.instance.patch<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async delete<T>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.delete<T>(url, mapConfig(config));
    return { data: res.data, status: res.status };
  }
}
